/* ============================================================
   Kaizen Seven — EFW PDP "How It Works"
   Desktop (>=900px): 4-up grid auto-cycles every 3s while in
   view; hover pins a step and replays its diagram.
   Mobile (<900px): clean vertical progression — cards are
   displayed in a vertical stack; scrolling a card into view
   triggers its animated SVG diagram naturally.
   ============================================================ */
(function () {
  'use strict';

  var CYCLE_DESKTOP_MS = 3000;

  function init(root) {
    if (root.dataset.efwHiwInit) return;
    root.dataset.efwHiwInit = '1';

    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-efw-hiw-panel]'));
    if (panels.length === 0) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var animate = !root.hasAttribute('data-efw-hiw-static');

    if (!animate || reduce) {
      panels.forEach(function (p) { p.classList.add('hiw-static', 'hiw-active'); });
      return;
    }

    var mqMobile = window.matchMedia('(max-width: 899.98px)');
    var mqHover = window.matchMedia('(hover: hover)');

    var idx = 0;
    var cleanup = null;

    function replay(p) {
      void p.offsetWidth; /* restart the diagram animation */
      p.classList.add('hiw-active');
    }

    /* ---------- Desktop: 4-up grid, auto-cycle + hover pin ---------- */
    function setupDesktop() {
      root.classList.add('efw-hiw--js', 'efw-hiw--desktop');
      var timer = null;
      var visible = false;
      var hoverHandlers = [];

      function setActive(i) {
        idx = i;
        panels.forEach(function (p, j) { p.classList.toggle('hiw-active', j === i); });
      }

      function start() {
        if (timer) return;
        setActive(idx);
        timer = setInterval(function () { setActive((idx + 1) % panels.length); }, CYCLE_DESKTOP_MS);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
        panels.forEach(function (p) { p.classList.remove('hiw-active'); });
      }

      function pin(i) {
        clearInterval(timer);
        timer = null;
        idx = i;
        panels.forEach(function (q) { q.classList.remove('hiw-active'); });
        replay(panels[i]);
      }

      panels.forEach(function (p, i) {
        function enter() { pin(i); }
        function leave() { if (visible && !timer) start(); }
        if (mqHover.matches) {
          p.addEventListener('mouseenter', enter);
          p.addEventListener('mouseleave', leave);
          hoverHandlers.push([p, 'mouseenter', enter], [p, 'mouseleave', leave]);
        }
        /* keyboard: focusing a panel pins it, like hover */
        p.addEventListener('focusin', enter);
        p.addEventListener('focusout', leave);
        hoverHandlers.push([p, 'focusin', enter], [p, 'focusout', leave]);
      });

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) start();
          else stop();
        });
      }, { threshold: 0.2 });
      io.observe(root);

      root._efwHiwNav = pin;

      return function () {
        io.disconnect();
        clearInterval(timer);
        hoverHandlers.forEach(function (h) {
          h[0].removeEventListener(h[1], h[2]);
        });
        panels.forEach(function (p) { p.classList.remove('hiw-active'); });
        root.classList.remove('efw-hiw--js', 'efw-hiw--desktop');
        delete root._efwHiwNav;
      };
    }

    /* ---------- Mobile: vertical scroll-triggered flow ---------- */
    function setupMobile() {
      root.classList.remove('efw-hiw--desktop');
      root.classList.add('efw-hiw--js');

      var animatedPanels = new Set();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var panel = entry.target;
            replay(panel);
            animatedPanels.add(panel);
          }
        });
      }, { threshold: 0.25 });

      panels.forEach(function (p) {
        io.observe(p);
      });

      root._efwHiwNav = function (i) {
        if (panels[i]) {
          replay(panels[i]);
          panels[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      return function () {
        io.disconnect();
        panels.forEach(function (p) { p.classList.remove('hiw-active'); });
        root.classList.remove('efw-hiw--js');
        delete root._efwHiwNav;
      };
    }

    function setMode() {
      if (cleanup) cleanup();
      idx = 0;
      cleanup = mqMobile.matches ? setupMobile() : setupDesktop();
    }
    setMode();
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', setMode);
    else if (mqMobile.addListener) mqMobile.addListener(setMode);
  }

  function initAll() {
    document.querySelectorAll('[data-efw-hiw]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init inside the Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-efw-hiw]');
    if (root) init(root);
  });

  // Theme editor: jump the stage/grid to the block selected in the sidebar
  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target && e.target.closest ? e.target.closest('[data-efw-hiw-panel]') : null;
    if (!panel) return;
    var root = panel.closest('[data-efw-hiw]');
    if (!root || !root._efwHiwNav) return;
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-efw-hiw-panel]'));
    root._efwHiwNav(panels.indexOf(panel));
  });
})();
